import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { createAsycudaPaymentApi, getAsycudaInnInfoApi } from '@http/payments';
import { selectedSenderAccountAC, senderAccountBalanceAC, senderAccountsModalOpenAC } from '@store/reducers/accounts';
import digitNumber from '@utils/digitNumber';
import { Button, DatePicker, Form, Input, Space } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SenderAccountsModal from '../modals/SenderAccountsModal';
import { getAccountBalanceApi, getSenderAccountsApi } from '@http/accounts';
import ConfirmPaymentModal from '../modals/ConfirmPaymentModal';
import { asycudaInnInfoAC, isConfirmPaymentModalOpenAC } from '@store/reducers/payments';
import { useNavigate } from 'react-router-dom';
import { otpConfirmAC, verifyTransIdAC, repeatAsycudaTransactionAC } from '@store/reducers/transactions';
import dayjs from 'dayjs';

const { TextArea } = Input;

const AsycudaForm = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [sendingAc, setSendingAc] = useState('');
  const [query, setQuery] = useState<any>({});

  const asycudaInnInfo = useAppSelector(state => state.paymentsReducer.asycudaInnInfo);
  const isAsycudaInnValid = useAppSelector(state => state.paymentsReducer.isAsycudaInnValid);
  const senderAccounts = useAppSelector(state => state.accountsReducer.senderAccounts);
  const senderAccountBalance = useAppSelector(state => state.accountsReducer.senderAccountBalance);
  const user = useAppSelector(state => state.appReducer.user);
  const repeatAsycudaTransaction = useAppSelector(state => state.transactionsReducer.repeatAsycudaTransaction);

  const innValue = user?.cust_info?.unique_id_value;

  const senderAccountSelect = (account: any) => {
    setSendingAc(account.value);
    form.setFieldValue('sending_ac', account.value);
    dispatch(selectedSenderAccountAC(account.value));
    dispatch(senderAccountsModalOpenAC(false));
    dispatch(getAccountBalanceApi('sender', { cust_ac_no: account.value }));
  };

  const submit = (values: any) => {
    dispatch(otpConfirmAC(false));
    setQuery(values);
    dispatch(isConfirmPaymentModalOpenAC(true));
  };

  const asycudaTransaction = () => {
    dispatch(
      createAsycudaPaymentApi(
        {
          ...query,
          ...asycudaInnInfo?.receiver_info,
          sending_amount: Number(query.sending_amount),
          tax_id: user?.cust_info?.unique_id_value,
          doc_date: query.doc_date?.format('DD.MM.YYYY'),
          sender_tax_id: asycudaInnInfo?.balance_info?.ownerAcc,
          sender_name: asycudaInnInfo?.balance_info?.ownerName,
        },
        navigate,
        () => {
          clearFields();
        }
      )
    );
  };

  const clearFields = () => {
    setSendingAc('');
    setQuery({});
    dispatch(asycudaInnInfoAC({}));
    dispatch(senderAccountBalanceAC(0));
    dispatch(isConfirmPaymentModalOpenAC(false));
    dispatch(selectedSenderAccountAC({}));
    dispatch(otpConfirmAC(false));
    dispatch(verifyTransIdAC(''));
    dispatch(repeatAsycudaTransactionAC({}));
    form.resetFields();
  };

  useEffect(() => {
    dispatch(
      getSenderAccountsApi({
        transaction_type: 'INTERBANK',
      })
    );
  }, []);

  useEffect(() => {
    dispatch(getAsycudaInnInfoApi(innValue));
  }, [innValue]);

  useEffect(() => {
    if (Object.keys(repeatAsycudaTransaction).length > 0) {
      setSendingAc(repeatAsycudaTransaction.sending_ac);

      form.setFieldsValue({
        sending_ac: repeatAsycudaTransaction.sending_ac,
        doc_date: repeatAsycudaTransaction.doc_date ? dayjs(repeatAsycudaTransaction.doc_date, 'DD.MM.YYYY') : null,
      });

      dispatch(selectedSenderAccountAC(repeatAsycudaTransaction.sending_ac));

      if (repeatAsycudaTransaction.sending_ac) {
        dispatch(
          getAccountBalanceApi('sender', {
            cust_ac_no: repeatAsycudaTransaction.sending_ac,
          })
        );
      }
    }
  }, [repeatAsycudaTransaction]);

  return (
    <div className='px-4 md:px-0'>
      {asycudaInnInfo?.balance_info?.errorCode === '0' || asycudaInnInfo?.balance_info?.errorCode === 0 ? (
        <div className='bg-slate-50 py-3 px-4 md:px-5 mt-4 md:mt-7 rounded-lg'>
          <p className='text-sm md:text-base text-slate-500 font-medium text-end'>
            {t('Balance')}: <b className='text-slate-900'>{digitNumber(asycudaInnInfo?.balance_info?.lastBalance)}</b>
          </p>
          <p className='text-sm md:text-base text-slate-500 font-medium text-end mt-2 md:mt-3'>
            {t('ID')}: <b className='text-slate-900'>{asycudaInnInfo?.balance_info?.ownerAcc}</b>
          </p>
          <p className='text-sm md:text-base text-slate-500 font-medium text-end mt-2 md:mt-3'>
            {t('Payer')}: <b className='text-slate-900'> {asycudaInnInfo?.balance_info?.ownerName}</b>
          </p>
        </div>
      ) : null}

      {!isAsycudaInnValid ? (
        <div className='bg-red-50 py-3 px-4 md:px-5 mt-4 md:mt-7 text-end text-sm text-red-500 rounded-lg'>
          {t('TinNotFoundInAsycuda')}
        </div>
      ) : (
        ''
      )}

      {asycudaInnInfo?.balance_info?.errorCode === '0' || asycudaInnInfo?.balance_info?.errorCode === 0 ? (
        <div className='mt-4 md:mt-7'>
          <div className='mb-4 md:mb-0 md:grid md:grid-cols-7 md:mt-7'>
            <p className='col-span-2 text-sm md:text-base text-slate-700 mb-2 md:mb-0'>{t('Bank name')}:</p>
            <div className='col-span-5'>
              <Input
                value={asycudaInnInfo?.receiver_info?.receiver_name}
                size='large'
                className='w-full'
                type='text'
                disabled
              />
            </div>
          </div>

          <div className='mb-4 md:mb-0 md:grid md:grid-cols-7 md:mt-7'>
            <p className='col-span-2 text-sm md:text-base text-slate-700 mb-2 md:mb-0'>{t('Document type')}:</p>
            <div className='col-span-5'>
              <Input
                value={asycudaInnInfo?.receiver_info?.doc_type}
                size='large'
                className='w-full'
                type='text'
                disabled
              />
            </div>
          </div>

          <div className='mb-4 md:mb-0 md:grid md:grid-cols-7 md:mt-7'>
            <p className='col-span-2 text-sm md:text-base text-slate-700 mb-2 md:mb-0'>{t('Recipient account')}:</p>
            <div className='col-span-5'>
              <Input
                value={asycudaInnInfo?.receiver_info?.receiving_ac}
                size='large'
                className='w-full'
                type='text'
                disabled
              />
            </div>
          </div>

          <Form form={form} name='InterbankOperations' onFinish={submit}>
            <div className='mb-4 md:mb-0 md:grid md:grid-cols-7 mt-4 md:mt-7'>
              <p className='col-span-2 text-sm md:text-base text-slate-700 mb-2 md:mb-0'>{t('From account')}:</p>
              <div className='col-span-5'>
                <Form.Item className='m-0' name='sending_ac' rules={[{ required: true, message: `${t('Required')}` }]}>
                  <Space.Compact size='large' className='w-full'>
                    <Input disabled className='w-16' size='large' value={'TJS'} />
                    <Input
                      value={sendingAc}
                      placeholder={`${t('Select account')}`}
                      onClick={() => dispatch(senderAccountsModalOpenAC(true))}
                      size='large'
                      className='flex-1'
                      type='text'
                      maxLength={20}
                    />
                  </Space.Compact>
                </Form.Item>
                {senderAccountBalance ? (
                  <p className='text-sm text-slate-700 font-medium text-end mt-2'>
                    {t('Balance')}: {digitNumber(senderAccountBalance)} {'TJS'}
                  </p>
                ) : null}
              </div>
            </div>

            <div className='mb-4 md:mb-0 md:grid md:grid-cols-7 mt-4 md:mt-7'>
              <p className='col-span-2 text-sm md:text-base text-slate-700 mb-2 md:mb-0'>{t('Document date')}:</p>
              <div className='col-span-5'>
                <Form.Item className='m-0' name='doc_date' rules={[{ required: true, message: `${t('Required')}` }]}>
                  <DatePicker format='DD.MM.YYYY' size='large' placeholder={`${t('Select date')}`} className='w-full' />
                </Form.Item>
              </div>
            </div>

            <div className='mb-4 md:mb-0 md:grid md:grid-cols-7 mt-4 md:mt-7'>
              <p className='col-span-2 text-sm md:text-base text-slate-700 mb-2 md:mb-0'>{t('Amount')}:</p>
              <div className='col-span-5'>
                <Form.Item
                  className='m-0 w-full'
                  name='sending_amount'
                  rules={[
                    { required: true, message: `${t('Required')}` },
                    {
                      pattern: /^[0-9]\d*(?:[.]\d{1,2})?$/,
                      message: `${t('Wrong amount')}`,
                    },
                    {
                      pattern: /^[1-9]\d*/,
                      message: `${t('Amount must be more 1')}`,
                    },
                  ]}
                >
                  <Space.Compact size='large' className='w-full'>
                    <Input disabled className='w-16' size='large' value={'TJS'} />
                    <Input
                      min={0}
                      maxLength={12}
                      placeholder={`${t('Enter amount')}`}
                      size='large'
                      className='flex-1'
                      type='text'
                    />
                  </Space.Compact>
                </Form.Item>
              </div>
            </div>

            <div className='mt-4 md:mt-5'>
              <p className='text-sm md:text-base text-slate-700 mb-2'>{t('Narrative')}:</p>
              <Form.Item
                className='m-0'
                name='narrative'
                rules={[
                  { required: true, message: `${t('Required')}` },
                  {
                    pattern: /^(?=.*\S).*/,
                    message: `${t('Enter symbol only')}`,
                  },
                ]}
              >
                <TextArea maxLength={140} rows={4} className='w-full' />
              </Form.Item>
            </div>

            <div className='flex justify-end mt-4 md:mt-5'>
              <Button type='primary' htmlType='submit' size='large' className='w-full md:w-auto'>
                {t('Confirm')}
              </Button>
            </div>
          </Form>

          <SenderAccountsModal accounts={senderAccounts} selectHandler={senderAccountSelect} />
          <ConfirmPaymentModal
            title='Custom payments'
            paymentInfo={{ ...query, ...asycudaInnInfo?.receiver_info, ...asycudaInnInfo?.balance_info }}
            currency={'TJS'}
            paymentTransactionHandler={asycudaTransaction}
            route={'asycudaPaymentStatus'}
            type={'ASYCUDA'}
            clearFields={clearFields}
          />
        </div>
      ) : null}
    </div>
  );
};

export default AsycudaForm;
